import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { isEmptyHtml } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { InterviewResultsActions } from '@recruitee/interview-results-data';
import type { InterviewResult } from '@recruitee/interview-results-types';
import { PlacementInfo } from '@recruitee/offers-ui';
import {
  DialogService,
  RtStatefulComponent,
  IconColorDirective,
  ButtonLoadingDirective,
  ButtonComponent,
  DividerComponent,
  IconButtonComponent,
  GroupComponent,
  HeadingComponent,
  BoxComponent,
  IconComponent,
} from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

import { InterviewKind } from '../interview-kind';
import { InterviewSummaryComponent } from '../interview-summary/interview-summary.component';

type State = {
  loading: boolean;
};

@Component({
  selector: 'rt-quick-evaluation-form',
  templateUrl: 'quick-evaluation-form.component.html',
  styleUrls: ['./quick-evaluation-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BoxComponent,
    NgTemplateOutlet,
    HeadingComponent,
    GroupComponent,
    IconButtonComponent,
    DividerComponent,
    InterviewSummaryComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    IconColorDirective,
    IconComponent,
    TranslatePipe,
  ],
})
export class QuickEvaluationFormComponent extends RtStatefulComponent<State> {
  @Input() public result: InterviewResult;
  @Input() public assignedPlacement?: PlacementInfo | null;
  @Input() public headerTpl: TemplateRef<any>;

  @Output() public close: EventEmitter<void> = new EventEmitter();

  public form: UntypedFormGroup;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private i18n: I18nService,
    private dialogSvc: DialogService,
    private viewRef: ViewContainerRef,
    protected cdRef: ChangeDetectorRef,
    private actions$: Actions,
    private store: Store,
  ) {
    super(cdRef);
    this.createState({
      loading: false,
    });
  }

  public ngOnChanges(): void {
    if (this.result && !this.form) {
      this.createEditForm();
      this.subscribeToEvents();
    }
  }

  public ngOnInit(): void {
    this.form
      .get('ratingNote')
      ?.valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(_ => this.cdRef.detectChanges());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancel(): void {
    this.close.emit();
  }

  public clearEmptyHtmlAndSave(): void {
    this.setState({
      loading: true,
    });

    this.clearEmptyHtml();

    this.store.dispatch(
      InterviewResultsActions.update({
        payload: {
          data: {
            id: this.result.id,
            value: { interviewResult: this.form.value },
          },
        },
      }),
    );
  }

  public delete(): void {
    const message = this.i18n.translate('app.interview.delete_message');
    const title = this.i18n.translate('app.interview.delete_title');

    this.openConfirmDialog(message, title, 'error').subscribe(confirmed => {
      if (confirmed) {
        this.deleteInterviewResult();
      }
    });
  }

  public onRateChange(): void {
    this.form.markAsDirty();
    this.cdRef.detectChanges();
  }

  private clearEmptyHtml(): void {
    const ratingNote: string = this.form.value.ratingNote ?? '';

    this.form.patchValue({
      ratingNote: isEmptyHtml(ratingNote) ? '' : ratingNote,
    });
  }

  private createEditForm(): void {
    this.form = this.formBuilder.group({
      kind: this.result?.kind || InterviewKind.QUICK,
      rating: [this.result?.rating || '', Validators.required],
      ratingNote: [this.result?.ratingNote || ''],
      offerId: this.result?.offerId || '',
      stageId: this.result?.stageId || '',
    });
  }

  private openConfirmDialog(message: string, title: string, variant: string): Observable<boolean> {
    return this.dialogSvc.confirm(
      { title, message, variant, isCancelable: true, safeFooter: true },
      this.viewRef,
    );
  }

  private deleteInterviewResult(): void {
    this.setState({
      loading: true,
    });

    this.store.dispatch(
      InterviewResultsActions.deleteOnePessimistic({
        payload: {
          data: {
            id: this.result.id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private subscribeToEvents(): void {
    this.actions$
      .pipe(
        ofType(
          InterviewResultsActions.deleteOnePessimisticSuccess,
          InterviewResultsActions.updateSuccess,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({
          loading: false,
        });
        this.close.emit();
      });

    this.actions$
      .pipe(
        ofType(
          InterviewResultsActions.deleteOnePessimisticFailed,
          InterviewResultsActions.updateFailed,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({
          loading: false,
        });
      });
  }
}
